import * as React from 'react';
import Alert from '@material-ui/lab/Alert';
import {
  Grid, Box, TextField, Divider, Card, CardContent,
  Typography,
  Button,
  CircularProgress,
} from '@material-ui/core';
import useSWR from 'swr';
import type { AxiosError } from 'axios';
import axios from 'axios';
import { useSnackbar } from 'notistack';
import { debounce } from 'lodash';
import {
  createStyles, darken, makeStyles, useTheme,
} from '@material-ui/core/styles';
import useEventListener from '../../lib/useEventListener';
import RepairCard from '../metalCollection/RepairCardCarousel';
import RepairInformation from '../metalCollection/RepairInformation';
import RepairJobContainer from './RepairJobContainer';
import type { IScannedRepair, IRepairItem } from '../marking/MarkingInterfaces';
import RejectWorkDialog from '../production/RejectWorkDialog';
import ApproveWorkDialog from './ApproveWorkDialog';

const useStyles = makeStyles((theme) => createStyles({
  root: {
    flexGrow: 1,
  },
  approveWorkButton: {
    color: theme.palette.common.white,
    backgroundColor: theme.palette.primary.main,
    '&:hover': {
      backgroundColor: theme.palette.primary.light,
    },
  },
  seePhotosButton: {
    color: '#587B9F',
    backgroundColor: '#D6E1EF',
    '&:hover': {
      backgroundColor: darken('#D6E1EF', 0.1),
    },
  },
}));

const Packer: React.FC = () => {
  const { enqueueSnackbar } = useSnackbar();
  const classes = useStyles();

  const theme = useTheme();
  const [barcode, setBarcode] = React.useState<string>();
  const [repairId, setRepairId] = React.useState<string>();
  const [workChecksRequired, setWorkChecksRequired] = React.useState<boolean>(false);
  const [showRejectWorkDialog, setShowRejectWorkDialog] = React.useState<boolean>(false);
  const [showApproveWorkDialog, setShowApproveWorkDialog] = React.useState<boolean>(false);
  const [destinationName, setDestinationName] = React.useState<string>('--');
  const [fetchingDestination, setFetchingDestination] = React.useState<boolean>(false);

  interface IDepartment {
    departmentName: string;
  }

  const handleUpdateDestination = () => {
    // Do not show destination name until this fetcher returns.
    setFetchingDestination(true);
    axios.get<IDepartment>('/packer/getDestinationDepartment', {
      params: {
        barcode,
      },
    }).then((response) => {
      const destName = response.data.departmentName;
      setDestinationName(destName);
      console.log(destName);
    })
      .catch((error: AxiosError) => {
        if (error?.response) {
          enqueueSnackbar(error?.response.data, { persist: false, variant: 'error' });
        }
      })
      .finally(() => {
        setFetchingDestination(false);
      });
  };

  const handleRejectWork = (rejectReason: string[]) => {
    axios.post('/packer/rejectWork', {
      barcode,
      RejectReasons: rejectReason,
    })
      .then(() => {
        handleUpdateDestination();
      })
      .catch((e: AxiosError) => {
        enqueueSnackbar(e?.response?.data, { persist: false, variant: 'error' });
      });
  };

  const handleApproveWork = () => {
    if (barcode) {
      axios.post<string>('/packer/approveWork', {
        barcode,
      }).then(() => {
        handleUpdateDestination();
      }).catch((e: AxiosError) => {
        enqueueSnackbar(e?.response?.data, { persist: false, variant: 'error' });
      });
    }
  };

  const repairFetcher = (url: string, repairIdParam: string) => axios.get<IScannedRepair>(url, {
    params: {
      repairId: repairIdParam,
    },
  }).then((res) => res.data)
    .catch((e: AxiosError) => {
      if (e.code === '404') {
        setBarcode(undefined);
      } else {
        setBarcode(undefined);
        enqueueSnackbar(e?.response?.data, { persist: false, variant: 'error' });
      }
    });

  const getRepairId = (repairBarcodeParam: string) => {
    const repairIdReturn = axios.get<{ repairId:string }>('/Packer/ScanRepair', {
      params: {
        barcode: repairBarcodeParam,
      },
    })
      .then((response) => (response.data.repairId))
      .catch((error: AxiosError) => {
        enqueueSnackbar(error?.response?.data, { persist: false, variant: 'error' });
      });
    return repairIdReturn;
  };

  const openRepair = debounce((barcodeInput: string) => {
    if (barcodeInput !== '') {
      setDestinationName('--');
      setBarcode(barcodeInput);
      getRepairId(barcodeInput)
        .then(
          (result) => { if (result) setRepairId(result); },
        )
        .catch(
          (error) => enqueueSnackbar(error, { persist: false, variant: 'error' }),
        );
    }
  }, 500);

  const { data } = useSWR(repairId ? ['/Packer/GetRepair', repairId] : null, (url, repairIdParam) => repairFetcher(url, repairIdParam));

  let barcodeBuffer = '';

  const handleBarcodeInput = (e: KeyboardEvent) => {
    if (e.key !== 'Shift') {
      barcodeBuffer = `${barcodeBuffer}${e.key}`;
      if (barcodeBuffer.includes('In:')) {
        barcodeBuffer = '';
      }
      if (barcodeBuffer.includes('{*|}')) {
        openRepair(barcodeBuffer.replace('{*|}', ''));
        barcodeBuffer = '';
      }
    }
  };

  useEventListener('keydown', handleBarcodeInput);

  const RepairItemMap = (
    data?.repairItems?.map((repairItem:IRepairItem) => (
      <Grid item xs={12}>
        <Card>
          <Box ml={2} my={2}>
            <Typography
              variant="h5"
              style={{ fontSize: '1.125rem', color: theme.palette.primary.main }}
            >
              {repairItem?.label || repairItem?.jewelryType?.label || repairItem?.repairItemType?.label}
            </Typography>
          </Box>
          <Divider />
          <CardContent style={{ backgroundColor: '#F5F5F5' }}>
            <Grid container spacing={0}>
              {
                repairItem?.repairItemJobs && repairItem?.repairItemJobs?.length >= 1
                && (
                <>
                  <Grid item xs={12}>
                    <Box>
                      <RepairJobContainer title="Work Requested" repairItem={repairItem} />
                    </Box>
                  </Grid>
                  <Grid item xs={12}>
                    <Box p={2} />
                  </Grid>
                </>
                )
              }
              <Grid item xs={12}>
                <Box>
                  <RepairInformation RepairItem={repairItem} />
                </Box>
              </Grid>
              <Grid item xs={12}>
                <Box p={2} />
              </Grid>
            </Grid>
          </CardContent>
        </Card>

      </Grid>
    ))
  );

  if (!data) {
    return (
      <Grid container direction="column" spacing={2} alignItems="center" justifyContent="center">
        <Grid item>
          <Alert severity="info">{'Scan in repair to review marking data, estimation data, and images of repair.'}</Alert>
        </Grid>
        <Grid item>
          <TextField
            variant="outlined"
            size="small"
            onKeyPress={
            (event) => {
              if (event.key === 'Enter') {
                event.preventDefault();
                event.stopPropagation();
                // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
                openRepair((event.target as HTMLInputElement).value);
              }
            }
            }
          />
        </Grid>
      </Grid>
    );
  }

  return (
    <Box>
      <Grid container spacing={0}>
        <Grid container item spacing={1} sm={12} md={9}>
          <Grid item sm={12} md={1} />
          <Grid container item spacing={2} sm={12} md={11}>
            <Grid item xs={12}>
              <Alert
                severity="info"
              >
                {'Review repair information, job information, and images of repair to determine if work is done correctly.'}
              </Alert>
            </Grid>
            <Grid item xs={12}>
              <RepairCard
                barcode={barcode || ''}
                destinationBinName={destinationName}
                secondaryText={
                  `${data.repairItems?.length} item${data.repairItems?.length === 1 ? '' : 's'}`
                }
              />
            </Grid>
            <Grid item xs={12} />
            {RepairItemMap}
            <Grid container direction="row" justifyContent="flex-end" item spacing={1}>
              <Box pr={4}>
                <Button
                  variant="text"
                  id="reject-work-button"
                  color="secondary"
                  onClick={() => { setShowRejectWorkDialog(true); }}
                >
                  {'WORK NOT APPROVED'}
                </Button>
              </Box>
              <Button
                id="approve-work-button"
                variant="contained"
                color="primary"
                className={classes.approveWorkButton}
                disabled={fetchingDestination}
                onClick={() => {
                  if (workChecksRequired) {
                    setShowApproveWorkDialog(true);
                  } else {
                    handleApproveWork();
                  }
                }}
              >
                {fetchingDestination ? <CircularProgress size={25} />
                  : 'WORK APPROVED'}
              </Button>
            </Grid>
          </Grid>

        </Grid>
      </Grid>
      <Grid>
        <Divider orientation="vertical" />
      </Grid>
      {barcode && (
      <>
        <RejectWorkDialog
          barcode={barcode}
          showModal={showRejectWorkDialog}
          onClose={() => setShowRejectWorkDialog(false)}
          rejectWork={handleRejectWork}
          station="packer"
        />
        <ApproveWorkDialog
          barcode={barcode}
          showModal={showApproveWorkDialog}
          onClose={() => setShowApproveWorkDialog(false)}
          approveWork={handleApproveWork}
          setWorkChecksRequired={setWorkChecksRequired}
        />
      </>
      )}
    </Box>
  );
};

export default Packer;
